import { useRef } from 'react';
import { Cpu } from 'lucide-react';
import type { EvaluationLine } from '../api/types';
import { pvSan, sanLabel } from '../board/notation';
import { ENGINE_LABEL } from '../engine/config';
import type { LocalAnalysisResult } from '../engine/useLocalAnalysis';
import {
  formatBarScore,
  fractionToPercent,
  whitePerspectiveScore,
  whiteWinFraction,
} from '../state/eval';
import { EngineSettingsButton } from './EngineSettings';

/**
 * The builder's eval bar and engine readout, fed by the browser engine
 * (`useLocalAnalysis`, ADR-0015).
 *
 * Everything shown here is computed on the viewer's device and is advisory: it is
 * labelled as local, never sent to the API, and never reaches the shared
 * `engine_evaluation` cache or deviation detection.
 */

function topLine(analysis: LocalAnalysisResult): EvaluationLine | null {
  return analysis.lines.find((line) => line.rank === 1) ?? analysis.lines[0] ?? null;
}

export interface EvalBarProps {
  analysis: LocalAnalysisResult;
  /** Side to move in the analysed position; engine scores are relative to it. */
  sideToMove: 'w' | 'b';
  /** Board orientation: White's share of the bar sits on White's side of the board. */
  orientation: 'w' | 'b';
}

export function EvalBar({ analysis, sideToMove, orientation }: EvalBarProps): JSX.Element {
  const line = topLine(analysis);
  const fraction = whiteWinFraction(line, sideToMove);

  // Between positions the engine briefly has no score. Holding the last fill (dimmed)
  // avoids the bar snapping to equal and back on every move.
  const held = useRef(0.5);
  if (fraction !== null) held.current = fraction;
  const percent = fractionToPercent(fraction ?? held.current);
  const pending = fraction === null;

  const whiteScore = line === null ? null : whitePerspectiveScore(line.score, sideToMove);
  const label = whiteScore === null ? null : formatBarScore(whiteScore);
  const whiteAhead = (fraction ?? held.current) >= 0.5;
  // The score is printed at the leading side's end of the bar, on its own colour.
  const labelAtBottom = whiteAhead === (orientation === 'w');

  return (
    <div
      className={`eval-gauge${orientation === 'b' ? ' eval-gauge-flipped' : ''}${
        pending ? ' eval-gauge-pending' : ''
      }`}
      role="img"
      aria-label={
        label === null
          ? `Evaluation pending from ${ENGINE_LABEL} in this browser`
          : `Evaluation ${label} for White, depth ${line?.depth ?? 0}, ${ENGINE_LABEL} in this browser`
      }
      data-testid="eval-bar"
    >
      <div className="eval-gauge-white" style={{ height: `${percent}%` }} />
      <div className="eval-gauge-midline" aria-hidden="true" />
      {label !== null && (
        <span
          className={`eval-gauge-label ${labelAtBottom ? 'eval-gauge-label-bottom' : 'eval-gauge-label-top'} ${
            whiteAhead ? 'eval-gauge-label-on-white' : 'eval-gauge-label-on-black'
          }`}
          aria-hidden="true"
        >
          {label}
        </span>
      )}
    </div>
  );
}

export interface EngineStripProps {
  analysis: LocalAnalysisResult;
  fen: string | null;
  sideToMove: 'w' | 'b';
  enabled: boolean;
  onToggle: (next: boolean) => void;
}

/**
 * Engine readout under the board: the best line on one row with the settings and
 * on/off switch, then every candidate line when the viewer asked for more than one.
 */
export function EngineStrip({
  analysis,
  fen,
  sideToMove,
  enabled,
  onToggle,
}: EngineStripProps): JSX.Element {
  const line = topLine(analysis);
  const whiteScore = line === null ? null : whitePerspectiveScore(line.score, sideToMove);
  const running = analysis.status === 'analysing' || analysis.status === 'loading';
  // `analysis.lines` is already limited to the viewer's "lines" setting.
  const candidates =
    enabled && fen !== null && analysis.lines.length > 1
      ? [...analysis.lines].sort((a, b) => a.rank - b.rank)
      : [];

  let body: JSX.Element;
  if (!enabled) {
    body = <span className="engine-strip-muted">Local engine off</span>;
  } else if (analysis.status === 'checking' || (analysis.status === 'loading' && line === null)) {
    body = (
      <span className="engine-strip-muted" role="status">
        Starting {ENGINE_LABEL}…
      </span>
    );
  } else if (analysis.status === 'missing') {
    body = (
      <span className="engine-strip-muted" title="Run npm run vendor:engine to add it">
        The local engine isn’t installed in this deployment
      </span>
    );
  } else if (analysis.status === 'unsupported') {
    body = <span className="engine-strip-muted">Local analysis unavailable: {analysis.error}</span>;
  } else if (analysis.status === 'error') {
    body = (
      <span className="engine-strip-error" role="alert">
        Local engine stopped: {analysis.error}
      </span>
    );
  } else if (line === null || whiteScore === null) {
    body = <span className="engine-strip-muted">Analysing…</span>;
  } else {
    body = (
      <>
        <span className="engine-strip-score">{formatBarScore(whiteScore)}</span>
        {line.bestMove && fen !== null && (
          <span>
            <span className="engine-strip-muted">Best </span>
            <span className="engine-strip-move">{sanLabel(fen, line.bestMove)}</span>
          </span>
        )}
        <span className="engine-strip-muted">
          depth {line.depth}
          {running ? '…' : ''}
        </span>
      </>
    );
  }

  return (
    <section className="engine-strip" aria-label="Local engine">
      <span className="engine-strip-source" title="Runs on this device; never sent to the server">
        <Cpu size={14} aria-hidden="true" />
        {ENGINE_LABEL} · local
      </span>
      <span className="engine-strip-body">{body}</span>
      <EngineSettingsButton />
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="Local engine analysis"
        title="Analyse positions in this browser as you browse"
        className="engine-switch"
        onClick={() => onToggle(!enabled)}
      >
        <span className="engine-switch-thumb" aria-hidden="true" />
      </button>
      {candidates.length > 0 && fen !== null && (
        <ol className="engine-pv-lines" aria-label="Engine lines">
          {candidates.map((candidate) => (
            <li key={candidate.rank}>
              <span className="engine-pv-score">
                {formatBarScore(whitePerspectiveScore(candidate.score, sideToMove))}
              </span>
              <span className="engine-pv-moves">{pvSan(fen, candidate.pv).join(' ')}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
